import { useEffect, useState } from 'react';
import { Loader2, ThumbsUp, ThumbsDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { fetchFeedback, type FeedbackEntry } from '@/api/admin';

export default function FeedbackTab() {
  const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const limit = 20;

  useEffect(() => {
    setLoading(true);
    fetchFeedback({ page, limit })
      .then((data) => {
        setFeedback(data.feedback);
        setTotal(data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  const totalPages = Math.ceil(total / limit);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[60px]">Rating</TableHead>
            <TableHead>Comment</TableHead>
            <TableHead>Thread</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {feedback.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground">
                No feedback found
              </TableCell>
            </TableRow>
          ) : (
            feedback.map((f) => (
              <TableRow key={f.id}>
                <TableCell>
                  {f.value === 1 ? (
                    <ThumbsUp className="h-4 w-4 text-green-500" />
                  ) : (
                    <ThumbsDown className="h-4 w-4 text-red-500" />
                  )}
                </TableCell>
                <TableCell className="max-w-[300px] truncate">
                  {f.comment || '—'}
                </TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {f.threadName || f.threadId || '—'}
                </TableCell>
                <TableCell>{f.userIdentifier || '—'}</TableCell>
                <TableCell>
                  {new Date(f.createdAt).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {total} feedback entries
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <span className="flex items-center text-sm px-2">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
